import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is Print on Demand and how does it work?',
      answer: 'Print on Demand (POD) is a fulfillment method where products are printed and shipped only after a customer places an order. This means you don\'t need to hold inventory or invest upfront in products. When you receive an order, it\'s automatically sent to us, we print the design on the product, perform quality checks, and ship directly to your customer. This allows you to offer a wide variety of products with custom designs without any inventory risk.'
    },
    {
      question: 'What are your shipping times and delivery options?',
      answer: 'Our standard processing time is 1-3 business days for most products. Shipping times vary depending on the destination: Domestic US orders typically arrive in 3-7 business days with standard shipping, or 1-3 days with express shipping. International orders usually take 7-21 business days depending on the destination country. We offer multiple shipping options including USPS, UPS, FedEx, and DHL to give you and your customers flexibility in delivery speed and cost.'
    },
    {
      question: 'Do you offer custom branding and packaging?',
      answer: 'Yes! We offer comprehensive custom branding solutions to help you create a memorable unboxing experience. This includes custom packaging boxes, branded tissue paper, custom packing slips and invoices, thank you cards and promotional inserts, branded shipping labels, and custom tape and stickers. Custom branding helps increase brand recognition and customer loyalty. Contact us to discuss your specific branding requirements and pricing.'
    },
    {
      question: 'What ecommerce platforms do you integrate with?',
      answer: 'We integrate seamlessly with all major ecommerce platforms including Shopify, WooCommerce, Etsy, Amazon, eBay, BigCommerce, Wix, Squarespace, and TikTok Shop. Our API also allows for custom integrations with any platform. Orders are automatically synced, processed, and tracking information is sent back to your store. Setup is quick and easy - most integrations can be completed in under 15 minutes.'
    },
    {
      question: 'What is your minimum order quantity?',
      answer: 'We have no minimum order quantity for most of our services. Whether you\'re selling one product or thousands, we\'re here to help. For Print on Demand, each order is printed individually, so there\'s truly no minimum. For bulk orders or wholesale fulfillment, we offer volume discounts starting at 100+ units. Contact our sales team for custom pricing on larger quantities.'
    },
    {
      question: 'How do returns and exchanges work?',
      answer: 'We handle returns and exchanges to make the process seamless for you and your customers. If a product is defective or damaged during shipping, we\'ll replace it at no cost. For customer-initiated returns (wrong size, changed mind, etc.), we can process returns to our warehouse and notify you of the condition. You can set your own return policy, and we\'ll help facilitate the process. Returned items in good condition can be restocked or reshipped.'
    },
    {
      question: 'What quality control measures do you have in place?',
      answer: 'Quality is our top priority. Every order goes through a multi-step quality control process: First, we verify the order details and product specifications. Then, for print products, we check print quality, color accuracy, and placement. Next, we inspect the physical product for defects before packaging. Finally, we verify correct packaging and shipping labels before dispatch. Our quality team maintains a 99.5% accuracy rate, and any issues are addressed immediately.'
    },
    {
      question: 'How do I get started with Black Mesa Mercantile?',
      answer: 'Getting started is easy! Simply contact us through our website or email support@Blackmesamer.com. We\'ll schedule a consultation call to understand your business needs and recommend the best solutions. Once you\'re ready, we\'ll help you integrate your store, set up your account, and guide you through uploading your products or sending inventory. Most businesses are up and running within 24-48 hours. There are no setup fees for standard accounts.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-green-600 font-semibold text-sm tracking-wider uppercase mb-3 block">
              FAQ
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Chivo'] font-bold text-gray-900 mb-6" data-testid="faq-hero-title">
              Frequently Asked{' '}
              <span className="text-green-600">Questions</span>
            </h1>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Find answers to common questions about our fulfillment services. 
              Can't find what you're looking for? Feel free to contact us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100"
                data-testid={`faq-item-${index}`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  data-testid={`faq-toggle-${index}`}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown 
                    size={20} 
                    className={`text-gray-500 flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-200 pt-4">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="contact-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                  <HelpCircle size={28} className="text-green-600" />
                </div>
                <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Can't find the answer you're looking for? Our friendly team is here to help. 
                  Reach out to us and we'll get back to you as soon as possible.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="faq-cta-contact"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
                  >
                    Contact Support
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="mailto:support@Blackmesamer.com"
                    data-testid="faq-email-link"
                    className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Email Us
                  </a>
                </div>
              </div>
              <div className="hidden md:block">
                <img
                  src="https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Customer support"
                  className="w-full rounded-2xl"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-['Chivo'] font-bold text-gray-900 mb-3">
              Explore More
            </h2>
            <p className="text-gray-600">
              Learn more about our services and how we can help your business grow.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { title: 'Our Services', description: 'Explore our comprehensive fulfillment solutions', link: '/services' },
              { title: 'About Us', description: 'Learn about our mission and values', link: '/about' },
              { title: 'Get Started', description: 'Ready to partner with us?', link: '/contact' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.link}
                  data-testid={`quick-link-${index}`}
                  className="block bg-gray-50 rounded-2xl p-6 hover:bg-gray-100 transition-colors group border border-gray-100 hover:border-green-200"
                >
                  <h3 className="font-['Chivo'] font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                  <span className="inline-flex items-center text-green-600 text-sm font-semibold">
                    Learn More
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
